import {
  ImageAssetSortOrder,
  ImageLibraryPageCacheEntry,
  ImageLibraryQueryKey,
  ImageState,
  UserImageLibraryCache,
  VSCAssetImageSourceName
} from 'js/types';
import { PLAY_FROM_EDITOR, PlayFromEditorAction } from 'js/actionCreators/playbackActions';

import {
  GET_IMAGE_LIBRARIES_SUCCESS,
  DELETE_USER_IMAGE_SUCCESS,
  GetImageLibrariesSuccessAction,
  DeleteUserImageSuccessAction,
  GET_IMAGE_LIBRARIES,
  GetImageLibrariesFailureAction,
  GET_IMAGE_LIBRARIES_FAILURE,
  GetImageLibrariesAction,
  PrependUserImagesListAction,
  PREPEND_USER_IMAGES_LIST,
  AddUserImageToScribeAction,
  START_REPLACE_IMAGE,
  StartReplaceImageAction,
  CLEAR_REPLACE_IMAGE,
  ClearReplaceImageAction,
  RENAME_USER_IMAGE_SUCCESS,
  RenameUserImageSuccessAction,
  SORT_IMAGE_ASSETS,
  SortImageAssetsAction,
  FETCH_IMAGE_LIBRARY_PAGE,
  FetchImageLibraryPageAction,
  FETCH_IMAGE_LIBRARY_PAGE_SUCCESS,
  FetchImageLibraryPageSuccessAction,
  FETCH_IMAGE_LIBRARY_PAGE_FAILURE,
  FetchImageLibraryPageFailureAction,
  INVALIDATE_IMAGE_LIBRARY_CACHE,
  InvalidateImageLibraryCacheAction
} from '../actionCreators/imagesActions';

const initialPagedLibrary: UserImageLibraryCache = {
  pages: {},
  loading: false,
  error: null
};

const invalidatePagesForSources = (
  pages: Record<string, ImageLibraryPageCacheEntry>,
  sources: VSCAssetImageSourceName[]
): Record<string, ImageLibraryPageCacheEntry> => {
  const sourcesSet = new Set(sources);
  return Object.fromEntries(
    Object.entries(pages).filter(([key]) => {
      const { queryKey } = JSON.parse(key) as { queryKey: ImageLibraryQueryKey };
      return !queryKey.source.some(s => sourcesSet.has(s));
    })
  );
};

const initialState: ImageState = {
  loadingLibraries: false,
  libraries: [],
  replacingImageElementId: null,
  imageAssetsSortOrder: 'lastUsed',
  pagedLibrary: initialPagedLibrary
};

type ImagesAction =
  | GetImageLibrariesSuccessAction
  | DeleteUserImageSuccessAction
  | GetImageLibrariesAction
  | GetImageLibrariesFailureAction
  | PrependUserImagesListAction
  | AddUserImageToScribeAction
  | StartReplaceImageAction
  | ClearReplaceImageAction
  | RenameUserImageSuccessAction
  | SortImageAssetsAction
  | FetchImageLibraryPageAction
  | FetchImageLibraryPageSuccessAction
  | FetchImageLibraryPageFailureAction
  | InvalidateImageLibraryCacheAction
  | SortImageAssetsAction
  | PlayFromEditorAction;

export default function imagesReducer(state = initialState, action: ImagesAction): ImageState {
  switch (action.type) {
    case GET_IMAGE_LIBRARIES: {
      return {
        ...state,
        loadingLibraries: true
      };
    }

    case GET_IMAGE_LIBRARIES_SUCCESS:
      return {
        ...state,
        loadingLibraries: false,
        libraries: action.libraries
      };

    case GET_IMAGE_LIBRARIES_FAILURE: {
      return {
        ...state,
        loadingLibraries: false
      };
    }

    case DELETE_USER_IMAGE_SUCCESS: {
      return {
        ...state,
        pagedLibrary: {
          ...state.pagedLibrary,
          pages: invalidatePagesForSources(state.pagedLibrary.pages, [action.targetLibrary])
        }
      };
    }

    case PREPEND_USER_IMAGES_LIST: {
      return {
        ...state,
        pagedLibrary: {
          ...state.pagedLibrary,
          pages: invalidatePagesForSources(state.pagedLibrary.pages, [action.targetLibrary])
        }
      };
    }

    case START_REPLACE_IMAGE: {
      return {
        ...state,
        replacingImageElementId: action.elementId
      };
    }

    case PLAY_FROM_EDITOR:
    case CLEAR_REPLACE_IMAGE: {
      return {
        ...state,
        replacingImageElementId: null
      };
    }

    case RENAME_USER_IMAGE_SUCCESS: {
      return {
        ...state,
        pagedLibrary: {
          ...state.pagedLibrary,
          pages: invalidatePagesForSources(state.pagedLibrary.pages, [action.targetLibrary])
        }
      };
    }

    case SORT_IMAGE_ASSETS: {
      return {
        ...state,
        imageAssetsSortOrder: action.sortOrder as ImageAssetSortOrder
      };
    }

    case FETCH_IMAGE_LIBRARY_PAGE: {
      return {
        ...state,
        pagedLibrary: { ...state.pagedLibrary, loading: true, error: null }
      };
    }

    case FETCH_IMAGE_LIBRARY_PAGE_SUCCESS: {
      return {
        ...state,
        pagedLibrary: {
          ...state.pagedLibrary,
          loading: false,
          pages: {
            ...state.pagedLibrary.pages,
            [action.cacheKey]: {
              items: action.items,
              total: action.total,
              hasMore: action.hasMore,
              expiresAt: action.expiresAt
            }
          }
        }
      };
    }

    case FETCH_IMAGE_LIBRARY_PAGE_FAILURE: {
      return {
        ...state,
        pagedLibrary: { ...state.pagedLibrary, loading: false, error: action.error }
      };
    }

    case INVALIDATE_IMAGE_LIBRARY_CACHE: {
      const pages = action.sources ? invalidatePagesForSources(state.pagedLibrary.pages, action.sources) : {};
      return {
        ...state,
        pagedLibrary: { ...state.pagedLibrary, pages }
      };
    }

    default:
      return state;
  }
}
